import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { addCandidateNote } from '../../services/taCandidateNotesApi';
import type { CandidateNote } from '../../types/taActionBoard';
import { NotesModal } from './NotesModal';

/**
 * One notes modal for the whole board.
 *
 * The 📝 chip lives on cards in several tabs; rather than thread modal state, the boardEmail and a
 * refresh callback through every tab and card, the provider owns a single modal and exposes
 * `openNotes`. A card just renders <NotesChip> and the chip calls openNotes — no per-tab wiring.
 */
interface NotesTarget {
  opportunityId: string;
  candidateName: string;
}

interface NotesApi {
  openNotes: (target: NotesTarget) => void;
}

const NotesCtx = createContext<NotesApi | null>(null);

/**
 * Null outside a provider (e.g. a shared card reused where notes aren't wired).
 *
 * It used to answer with a no-op `openNotes`, which was safe only because the chip hid itself
 * unless the candidate already had notes. Now that it renders on every card, a no-op would put a
 * dead button on any card mounted outside the provider — so callers check and render nothing.
 */
export function useNotes(): NotesApi | null {
  return useContext(NotesCtx);
}

export function NotesProvider({
  boardEmail,
  children,
}: {
  boardEmail: string;
  children: ReactNode;
}) {
  const [target, setTarget] = useState<NotesTarget | null>(null);

  const openNotes = useCallback((t: NotesTarget) => setTarget(t), []);

  /**
   * ⚠️ Closing no longer reloads the board, and the `onChanged` prop that did it is gone.
   *
   * It existed because the chip carried a COUNT that the board payload supplied, so a new note
   * left a stale chip until the next build. The chip has no count now (Theresa, Oct 2026) and the
   * modal reads the notes itself every time it opens — so nothing on screen can go stale, and
   * rebuilding a whole board because somebody typed one note is the exact cost this change
   * exists to remove.
   */
  const close = useCallback(() => setTarget(null), []);

  const onAdd = useCallback(
    async (value: string): Promise<CandidateNote> => {
      if (!target) throw new Error('no candidate selected');
      return addCandidateNote({ opportunityId: target.opportunityId, boardEmail, value });
    },
    [target, boardEmail]
  );

  return (
    <NotesCtx.Provider value={{ openNotes }}>
      {children}
      {target && (
        <NotesModal
          candidateName={target.candidateName}
          opportunityId={target.opportunityId}
          onClose={close}
          onAdd={onAdd}
        />
      )}
    </NotesCtx.Provider>
  );
}
